import { useRef, useState } from "react";
import { api, ApiError } from "./api";
import { t, virheTeksti } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   Hitsauslokin muokkauskierto: vie Excel → korjaa → tuo takaisin.
   Kaksi arkkia (Hitsit + Osat), avain hitsi_id / osa_id. Tuonti on
   kaksivaiheinen: kuivakäynti näyttää erot, vahvistus tallentaa.
   Tyhjä solu ei muuta mitään — turvallinen korjauskierto.
   ═══════════════════════════════════════════════════════════ */

type Tulos = Awaited<ReturnType<typeof api.tuoHitsauslokiKorjaukset>>;

export default function HitsauslokiMuokkaus({ kohde_id, upotettu = false }: { kohde_id?: number; /** T651: napparivin alla, aina auki ilman omaa otsikkoa. */ upotettu?: boolean }) {
  const [aukiTila, setAuki] = useState(false);
  const auki = upotettu || aukiTila;
  const [sisalto, setSisalto] = useState<string | null>(null);
  const [tiedostonimi, setTiedostonimi] = useState("");
  const [esikatselu, setEsikatselu] = useState<Tulos | null>(null);
  const [tallennettu, setTallennettu] = useState<Tulos | null>(null);
  const [tyoskentelee, setTyoskentelee] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function nollaa() {
    setSisalto(null); setTiedostonimi(""); setEsikatselu(null);
    setTallennettu(null); setVirhe(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  async function valitse(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setVirhe(null); setEsikatselu(null); setTallennettu(null);
    const lukija = new FileReader();
    lukija.onload = async () => {
      const data = String(lukija.result);
      setSisalto(data); setTiedostonimi(f.name);
      setTyoskentelee(true);
      try {
        setEsikatselu(await api.tuoHitsauslokiKorjaukset(data, false));
      } catch (err) {
        setVirhe(err instanceof ApiError ? virheTeksti(err.virhe, err.message) : t("muu.pohja.luku_virhe"));
      } finally { setTyoskentelee(false); }
    };
    lukija.readAsDataURL(f);
  }

  async function vahvista() {
    if (!sisalto) return;
    setTyoskentelee(true); setVirhe(null);
    try {
      setTallennettu(await api.tuoHitsauslokiKorjaukset(sisalto, true));
      setEsikatselu(null);
    } catch (err) {
      setVirhe(err instanceof ApiError ? virheTeksti(err.virhe, err.message) : t("ltar.tallennus_virhe"));
    } finally { setTyoskentelee(false); }
  }

  const muutoksia = (esikatselu?.kenttamuutoksia ?? 0) > 0;

  return (
    <section className="panel" style={{ marginBottom: 16 }}>
      {!upotettu && <div className="panel__head">
        <h2>{t("muu.hlm.otsikko")}</h2>
        <button className="nappi nappi--kevyt" onClick={() => { setAuki(!auki); if (auki) nollaa(); }}>
          {auki ? t("hl.sulje") : t("qr.avaa")}
        </button>
      </div>}

      {auki && (
        <div style={{ padding: "4px 2px" }}>
          <p className="dim" style={{ fontSize: 13, marginTop: 0 }}>
            {t("muu.hlm.selite1")} <b>{t("muu.hlm.selite2")}</b> —
            {" "}{t("muu.hlm.selite3")}
          </p>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", margin: "10px 0" }}>
            <a className="nappi nappi--paa" href={api.hitsauslokiMuokkauspohja(kohde_id ? { kohde_id } : {})}>
              ⬇ {t("muu.hlm.vie")}{kohde_id ? t("muu.hlm.tama_kohde") : t("muu.hlm.kaikki")}
            </a>
            <input ref={fileRef} type="file" accept=".xlsx" style={{ display: "none" }} onChange={valitse} />
            <button className="nappi nappi--kevyt" disabled={tyoskentelee}
              onClick={() => fileRef.current?.click()}>
              ⬆ {t("muu.hlm.tuo")}
            </button>
            {tiedostonimi && <span className="dim" >{tiedostonimi}</span>}
            {(esikatselu || tallennettu) && (
              <button className="nappi nappi--kevyt" onClick={nollaa}>{t("muu.taulukko.tyhjenna")}</button>
            )}
          </div>

          {tyoskentelee && <p className="dim">{t("muu.pohja.kasitellaan")}</p>}
          {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}

          {esikatselu && !tallennettu && (
            <div style={{ border: "1px solid var(--viiva)", padding: 12, marginTop: 6 }}>
              <div style={{ fontSize: 13, marginBottom: 8 }}>
                {t("muu.hlm.kuivakaynti")} — <b>{esikatselu.rivimuutoksia ?? 0}</b> {t("muu.hlm.rivia_pilkku")}{" "}
                <b>{esikatselu.kenttamuutoksia ?? 0}</b> {t("muu.hlm.kenttamuutosta")}
                {esikatselu.hitsit && <> {t("muu.hlm.hitsit_rivia", { n: esikatselu.hitsit.rivit })}
                  {esikatselu.hitsit.tuntematon > 0 && ` ${t("muu.hlm.tuntematon_id", { n: esikatselu.hitsit.tuntematon })}`}.</>}
                {esikatselu.osat && <> {t("muu.hlm.osat_rivia", { n: esikatselu.osat.rivit })}
                  {esikatselu.osat.tuntematon > 0 && ` ${t("muu.hlm.tuntematon", { n: esikatselu.osat.tuntematon })}`}.</>}
              </div>

              {muutoksia ? (
                <>
                  <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid var(--viiva)" }}>
                    <table className="tbl" style={{ fontSize: 12, width: "100%" }}>
                      <thead>
                        <tr><th>{t("muu.hlm.taulu")}</th><th>id</th><th>{t("muu.pohja.kentta")}</th><th>{t("muu.hlm.vanha")}</th><th>{t("muu.hlm.uusi")}</th></tr>
                      </thead>
                      <tbody>
                        {esikatselu.esikatselu?.map((m, i) => (
                          <tr key={i}>
                            <td>{m.taulu === "hitsi" ? t("muu.hlm.hitsi") : t("muu.hlm.osa")}</td>
                            <td className="mono">{m.id}</td>
                            <td>{m.kentta}</td>
                            <td className="dim">{m.vanha || "—"}</td>
                            <td><b>{m.uusi}</b></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {(esikatselu.kenttamuutoksia ?? 0) > (esikatselu.esikatselu?.length ?? 0) && (
                    <p className="dim" >
                      {t("muu.hlm.naytetaan", { n: esikatselu.esikatselu?.length ?? 0 })}
                    </p>
                  )}
                  <button className="nappi nappi--paa" style={{ marginTop: 10 }}
                    disabled={tyoskentelee} onClick={vahvista}>
                    {t("muu.hlm.vahvista", { n: esikatselu.kenttamuutoksia ?? 0 })}
                  </button>
                </>
              ) : (
                <p className="dim">{t("muu.hlm.ei_muutoksia")}</p>
              )}
            </div>
          )}

          {tallennettu && (
            <p className="huom" role="status" style={{ marginTop: 8 }}>
              {t("muu.hlm.tallennettu", { n: tallennettu.paivitetty ?? 0, m: tallennettu.kenttamuutoksia ?? 0 })}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
